import { eintragNachId, textSchluessel } from '../../../services/mangelKatalog/auswahl';
import { OVERLAY_WARNGRENZE_BYTES } from '../../../services/mangelKatalog/editorLogik';
import { falten, suchen } from '../../../services/mangelKatalog/suche';
import type {
    AenderbaresFeld,
    AufgeloesterKatalog,
    AufgeloesterKnoten,
    Dringlichkeit,
    Gesamtkatalog,
    KatalogEintrag,
    KatalogKnoten,
    KatalogOverlay,
    KnotenTyp,
    StandardKatalog,
} from '../../../services/mangelKatalog/typen';
import { katalogAufloesen, leeresOverlay } from '../../../services/mangelKatalog/ueberlagerung';

/**
 * Ansicht des Admin-Editors für den Firmen-Mängelkatalog (Paket H), rein
 * funktional: Baum samt ausgeblendeter Knoten, Merker je Zeile, Formular
 * ↔ Overlay-Felder, Treffer der Suche im Gesamtkatalog. Die Overlay-Operationen selbst
 * stehen in services/mangelKatalog/editorLogik.ts.
 */

type Felder = Partial<Pick<KatalogKnoten, AenderbaresFeld>>;

// ─── Baum ───────────────────────────────────────────────────────────────────

export interface EditorKatalog {
    /**
     * Standard + Overlay OHNE Ausblenden: alle Knoten, auch ausgeblendete, mit
     * wirksamer Reihenfolge, Namen und Piktogrammen. Der Editor zeigt diesen
     * Baum und sortiert auch darin (ausgeblendete Geschwister zählen mit).
     */
    voll: AufgeloesterKatalog;
    /** Ausgeblendet samt vererbter Knoten (aus der wirksamen Auflösung). */
    ausgeblendet: ReadonlySet<string>;
    /** Im Overlay selbst ausgeblendet (nur diese lassen sich einblenden). */
    direktAusgeblendet: ReadonlySet<string>;
    /** Standardeinträge neuer als overlay.gesehenBisVersion. */
    neuAnzahl: number;
    /** Knoten-ID → neue Einträge im Teilbaum (der Knoten selbst zählt mit). */
    neuImTeilbaum: ReadonlyMap<string, number>;
    /** Standard ohne Overlay: Vergleich für „geändert“ und „Auf Standard zurücksetzen“. */
    standard: AufgeloesterKatalog;
}

const standardCache = new WeakMap<StandardKatalog, AufgeloesterKatalog>();

/** Standard ohne jedes Overlay (je Standard-Katalog einmal aufgelöst). */
export function standardAufgeloest(standard: StandardKatalog): AufgeloesterKatalog {
    const vorhanden = standardCache.get(standard);
    if (vorhanden) return vorhanden;
    const k = katalogAufloesen(standard, null);
    standardCache.set(standard, k);
    return k;
}

export function editorKatalogBauen(standard: StandardKatalog, overlay: KatalogOverlay | null): EditorKatalog {
    const wirksam = katalogAufloesen(standard, overlay);
    const voll = overlay ? katalogAufloesen(standard, { ...overlay, ausgeblendet: [] }) : wirksam;
    const neuImTeilbaum = new Map<string, number>();
    const zaehlen = (id: string): number => {
        let n = voll.knoten.get(id)?.neu ? 1 : 0;
        for (const kind of voll.kinder.get(id) ?? []) n += zaehlen(kind);
        neuImTeilbaum.set(id, n);
        return n;
    };
    let neuAnzahl = 0;
    for (const id of voll.kinder.get('') ?? []) neuAnzahl += zaehlen(id);
    return {
        voll,
        ausgeblendet: wirksam.ausgeblendet,
        direktAusgeblendet: new Set(overlay?.ausgeblendet ?? []),
        neuAnzahl,
        neuImTeilbaum,
        standard: standardAufgeloest(standard),
    };
}

export type Merker = 'neu' | 'eigen' | 'geaendert' | 'ausgeblendet';

export const MERKER_TEXT: Readonly<Record<Merker, string>> = Object.freeze({
    neu: 'neu',
    eigen: 'eigen',
    geaendert: 'geändert',
    ausgeblendet: 'ausgeblendet',
});

export interface BaumZeile {
    knoten: AufgeloesterKnoten;
    /** 0 = Kategorie, 1 = Gruppe, 2 = Mangel (Tiefe im Baum). */
    ebene: number;
    merker: Merker[];
    /** Im Overlay selbst ausgeblendet. */
    selbstAusgeblendet: boolean;
    /** Nur über einen Oberpunkt ausgeblendet. */
    geerbtAusgeblendet: boolean;
    kinder: readonly string[];
    /** Neue Einträge unterhalb (ohne den Knoten selbst). */
    neuDarunter: number;
    kannHoch: boolean;
    kannRunter: boolean;
    /** Kurztext (Mangel) bzw. Name (Kategorie/Gruppe), einzeilig. */
    beschriftung: string;
}

/** Kurztext ohne Zeilenumbruch ('Seiten-\nschutz' → 'Seitenschutz'). */
export const einzeilig = (kurz: string): string =>
    (kurz ?? '').replace(/-\n/g, '').replace(/\s*\n\s*/g, ' ').trim();

export function zeileBauen(ek: EditorKatalog, id: string): BaumZeile | null {
    const k = ek.voll.knoten.get(id);
    if (!k) return null;
    const selbst = ek.direktAusgeblendet.has(id);
    const aus = selbst || ek.ausgeblendet.has(id);
    const merker: Merker[] = [];
    if (k.neu) merker.push('neu');
    if (k.herkunft === 'eigen') merker.push('eigen');
    if (k.geaendert) merker.push('geaendert');
    if (aus) merker.push('ausgeblendet');
    const geschwister = ek.voll.kinder.get(k.eltern ?? '') ?? [];
    const i = geschwister.indexOf(id);
    return {
        knoten: k,
        ebene: Math.max(0, k.pfad.length - 1),
        merker,
        selbstAusgeblendet: selbst,
        geerbtAusgeblendet: aus && !selbst,
        kinder: ek.voll.kinder.get(id) ?? [],
        neuDarunter: (ek.neuImTeilbaum.get(id) ?? 0) - (k.neu ? 1 : 0),
        kannHoch: i > 0,
        kannRunter: i >= 0 && i < geschwister.length - 1,
        beschriftung: k.typ === 'mangel' ? einzeilig(k.kurz) || k.name : k.name,
    };
}

/** Typ eines eigenen Kindes: unter einer Kategorie eine Gruppe, unter einer Gruppe ein Mangel. */
export function kindTyp(elternTyp: KnotenTyp): KnotenTyp | null {
    if (elternTyp === 'kategorie') return 'gruppe';
    if (elternTyp === 'gruppe') return 'mangel';
    return null;
}

/** Ein leeres Overlay, das ab jetzt alle Standardeinträge bis `version` als gesehen führt. */
export function neuesOverlay(version: number): KatalogOverlay {
    return { ...leeresOverlay(), gesehenBisVersion: version };
}

/**
 * Roher Knoten, wie er gerade gilt: eigener Knoten oder Standardknoten mit
 * den Feldern aus overlay.geaendert. Unbekannt → null.
 */
export function rohKnoten(standard: StandardKatalog, overlay: KatalogOverlay | null, id: string): KatalogKnoten | null {
    const eigen = (overlay?.eigene ?? []).find((k) => k.id === id);
    if (eigen) return eigen;
    const std = (standard?.knoten ?? []).find((k) => k.id === id);
    if (!std) return null;
    const felder = overlay?.geaendert?.[id]?.felder;
    return felder ? { ...std, ...felder } : std;
}

// ─── Formular ───────────────────────────────────────────────────────────────

export interface Formular {
    kurz: string;
    /** Nur Kategorie/Gruppe; leer = Kurztext. */
    name: string;
    /** '' = kein eigenes Piktogramm (Standard bzw. vom Oberpunkt geerbt). */
    pikto: string;
    titel: string;
    text: string;
    /** Gekürzte Vorschrift, so wie sie in den Bericht kommt. */
    vorschrift: string;
    dringlichkeit: '' | Dringlichkeit;
    /** Eingabe als Text; zählt nur bei 'frist'. */
    fristTage: string;
    /**
     * Englische Fassung (optional, 28.09.): Kurztext, Name (Kategorie/Gruppe),
     * Titel und Mängeltext (Mangel). Leer = englischer Standard, sonst deutsch.
     */
    kurzEn: string;
    nameEn: string;
    titelEn: string;
    textEn: string;
}

export const KURZ_MAX_MANGEL = 28;
export const KURZ_MAX_ZEILE = 11;
export const NAME_MAX = 32;
export const FRIST_MIN = 1;
export const FRIST_MAX = 90;

export function leeresFormular(): Formular {
    return {
        kurz: '', name: '', pikto: '', titel: '', text: '', vorschrift: '', dringlichkeit: '', fristTage: '',
        kurzEn: '', nameEn: '', titelEn: '', textEn: '',
    };
}

/** Englischer Standard eines Knotens (Beschriftung und Texte), leer bei eigenen Knoten. */
export type EnglischerStandardText = Partial<Record<'kurz' | 'name' | 'titel' | 'text', string>>;

/**
 * Englischer Standard eines Standardknotens aus standard.englisch: Kurztext
 * und Name des Knotens, Titel/Text des Knotens bzw. seines Eintrags (quelle[0]).
 */
export function englischerStandardVon(standard: StandardKatalog, id: string): EnglischerStandardText {
    const en = standard?.englisch;
    const std = (standard?.knoten ?? []).find((k) => k.id === id);
    if (!en || !std) return {};
    const knoten = en.knoten[id];
    const auszug = std.typ === 'mangel' && std.quelle?.[0] ? en.auszug[std.quelle[0]] : undefined;
    const aus: EnglischerStandardText = {};
    if (knoten?.kurz) aus.kurz = knoten.kurz;
    if (knoten?.name) aus.name = knoten.name;
    const titel = knoten?.titel ?? auszug?.titel;
    if (titel) aus.titel = titel;
    const text = knoten?.text ?? auszug?.text;
    if (text) aus.text = text;
    return aus;
}

/**
 * Formularwerte eines Knotens (aufgelöst für die Texte der Vorlage, roh für die
 * eigenen Felder). Englisch: Felder des Overlays bzw. eigenen Knotens, sonst der
 * englische Standard (`englisch`), sonst leer.
 */
export function formularAus(k: AufgeloesterKnoten | undefined, roh: KatalogKnoten | null, englisch: EnglischerStandardText = {}): Formular {
    return {
        kurz: roh?.kurz ?? k?.kurz ?? '',
        name: roh?.name ?? '',
        pikto: roh?.pikto ?? '',
        titel: k?.vorlage?.titel ?? roh?.titel ?? '',
        text: k?.vorlage?.text ?? roh?.text ?? '',
        vorschrift: k?.vorlage?.vorschrift ?? roh?.vorschrift ?? '',
        dringlichkeit: roh?.dringlichkeit ?? '',
        fristTage: typeof roh?.fristTage === 'number' ? String(roh.fristTage) : '',
        kurzEn: roh?.kurzEn ?? englisch.kurz ?? '',
        nameEn: roh?.nameEn ?? englisch.name ?? '',
        titelEn: roh?.titelEn ?? englisch.titel ?? '',
        textEn: roh?.textEn ?? englisch.text ?? '',
    };
}

/** Mangel: eine Zeile. Kategorie/Gruppe: Zeilen getrimmt, leere weg (höchstens zwei, prüft formularPruefen). */
export function kurzNormalisieren(kurz: string, typ: KnotenTyp): string {
    if (typ === 'mangel') return (kurz ?? '').replace(/\s*\n\s*/g, ' ').trim();
    return (kurz ?? '')
        .split('\n')
        .map((z) => z.trim())
        .filter(Boolean)
        .join('\n');
}

const fristLesen = (s: string): number | null => {
    const t = s.trim();
    if (!/^\d+$/.test(t)) return null;
    const n = Number.parseInt(t, 10);
    return n >= FRIST_MIN && n <= FRIST_MAX ? n : null;
};

/** Fehlermeldung oder null. Die Grenzen sind die der Torte und der Kacheln. */
export function formularPruefen(f: Formular, typ: KnotenTyp): string | null {
    const kurz = kurzNormalisieren(f.kurz, typ);
    if (!kurz) return 'Kurztext fehlt.';
    if (typ === 'mangel') {
        if (kurz.length > KURZ_MAX_MANGEL) return `Kurztext der Kachel: höchstens ${KURZ_MAX_MANGEL} Zeichen.`;
    } else {
        const zeilen = kurz.split('\n');
        if (zeilen.length > 2) return 'Kurztext des Tortenstücks: höchstens zwei Zeilen.';
        if (zeilen.some((z) => z.length > KURZ_MAX_ZEILE)) {
            return `Kurztext des Tortenstücks: je Zeile höchstens ${KURZ_MAX_ZEILE} Zeichen (Umbruch mit Enter, Trennstrich am Zeilenende).`;
        }
        if (f.name.trim().length > NAME_MAX) return `Name: höchstens ${NAME_MAX} Zeichen.`;
    }
    if (falten(einzeilig(kurz)) === 'sonstiges' || (typ !== 'mangel' && falten(f.name) === 'sonstiges')) {
        return '„Sonstiges“ ist für das Blätter-Stück der Torte reserviert.';
    }
    if (typ === 'mangel') {
        if (!f.titel.trim() && !f.text.trim()) return 'Titel oder Mängeltext nötig.';
        if (f.dringlichkeit === 'frist' && fristLesen(f.fristTage) === null) {
            return `Frist: ganze Zahl von ${FRIST_MIN} bis ${FRIST_MAX} Tagen.`;
        }
    }
    return englischPruefen(f, typ);
}

/** Englische Felder (optional): dieselben Grenzen wie deutsch; „More“ ist das Blätter-Stück. */
function englischPruefen(f: Formular, typ: KnotenTyp): string | null {
    const kurzEn = kurzNormalisieren(f.kurzEn, typ);
    if (kurzEn) {
        if (typ === 'mangel') {
            if (kurzEn.length > KURZ_MAX_MANGEL) return `Englischer Kurztext der Kachel: höchstens ${KURZ_MAX_MANGEL} Zeichen.`;
        } else {
            const zeilen = kurzEn.split('\n');
            if (zeilen.length > 2) return 'Englischer Kurztext des Tortenstücks: höchstens zwei Zeilen.';
            if (zeilen.some((z) => z.length > KURZ_MAX_ZEILE)) {
                return `Englischer Kurztext des Tortenstücks: je Zeile höchstens ${KURZ_MAX_ZEILE} Zeichen.`;
            }
        }
    }
    if (typ !== 'mangel' && f.nameEn.trim().length > NAME_MAX) return `Englischer Name: höchstens ${NAME_MAX} Zeichen.`;
    if (falten(einzeilig(kurzEn)) === 'more' || (typ !== 'mangel' && falten(f.nameEn) === 'more')) {
        return '„More“ ist für das Blätter-Stück der englischen Torte reserviert.';
    }
    return null;
}

const FELDER_MANGEL: readonly AenderbaresFeld[] = [
    'kurz', 'pikto', 'titel', 'text', 'vorschrift', 'dringlichkeit', 'fristTage', 'kurzEn', 'titelEn', 'textEn',
];
const FELDER_OBERPUNKT: readonly AenderbaresFeld[] = ['kurz', 'name', 'pikto', 'kurzEn', 'nameEn'];

/** Felder, die das Formular für diesen Typ pflegt. */
export const felderFuer = (typ: KnotenTyp): readonly AenderbaresFeld[] => (typ === 'mangel' ? FELDER_MANGEL : FELDER_OBERPUNKT);

function feldSetzen<F extends AenderbaresFeld>(ziel: Felder, feld: F, wert: Felder[F]): void {
    ziel[feld] = wert;
}

/** Normalisierte Werte (leer → undefined) für die Felder des Typs. */
function normalisiert(f: Formular, typ: KnotenTyp): Felder {
    const n: Felder = {};
    feldSetzen(n, 'kurz', kurzNormalisieren(f.kurz, typ) || undefined);
    feldSetzen(n, 'pikto', f.pikto.trim() || undefined);
    feldSetzen(n, 'kurzEn', kurzNormalisieren(f.kurzEn, typ) || undefined);
    if (typ !== 'mangel') {
        feldSetzen(n, 'name', f.name.trim() || undefined);
        feldSetzen(n, 'nameEn', f.nameEn.trim() || undefined);
        return n;
    }
    feldSetzen(n, 'titelEn', f.titelEn.trim() || undefined);
    feldSetzen(n, 'textEn', f.textEn.trim() || undefined);
    feldSetzen(n, 'titel', f.titel.trim() || undefined);
    feldSetzen(n, 'text', f.text.trim() || undefined);
    feldSetzen(n, 'vorschrift', f.vorschrift.trim() || undefined);
    feldSetzen(n, 'dringlichkeit', f.dringlichkeit || undefined);
    feldSetzen(n, 'fristTage', f.dringlichkeit === 'frist' ? fristLesen(f.fristTage) ?? undefined : undefined);
    return n;
}

/**
 * Formular → Felder für `knotenAendern`. Jedes Feld des Typs steht im
 * Ergebnis; `undefined` nimmt eine Änderung zurück.
 * - Standardknoten (`vergleich` = Werte ohne Overlay): gleiche oder leere
 *   Werte → undefined, das Overlay trägt nur echte Abweichungen.
 * - Eigene Knoten (`vergleich` null): Werte direkt, leer → undefined.
 */
export function formularZuFelder(f: Formular, typ: KnotenTyp, vergleich: Formular | null): Felder {
    const neu = normalisiert(f, typ);
    const alt = vergleich ? normalisiert(vergleich, typ) : null;
    const felder: Felder = {};
    for (const feld of felderFuer(typ)) {
        const wert = neu[feld];
        feldSetzen(felder, feld, alt !== null && wert === alt[feld] ? undefined : wert);
    }
    return felder;
}

/** Eigener Knoten aus dem Formular (ohne ID; nur gesetzte Felder). */
export function eigenerKnotenAusFormular(f: Formular, typ: KnotenTyp, eltern: string | null): Omit<KatalogKnoten, 'id'> {
    const felder = formularZuFelder(f, typ, null);
    const k: Omit<KatalogKnoten, 'id'> = { typ, eltern: typ === 'kategorie' ? null : eltern, kurz: felder.kurz ?? '' };
    for (const feld of felderFuer(typ)) {
        if (feld === 'kurz') continue;
        const wert = felder[feld];
        if (wert !== undefined) feldSetzen(k, feld, wert);
    }
    return k;
}

// ─── Kopf ───────────────────────────────────────────────────────────────────

/** '0,4 KB' — eine Nachkommastelle, deutsches Komma. */
export function groesseText(bytes: number): string {
    const kb = Math.max(0, Number.isFinite(bytes) ? bytes : 0) / 1024;
    return `${kb.toFixed(1).replace('.', ',')} KB`;
}

export const istZuGross = (bytes: number): boolean => bytes >= OVERLAY_WARNGRENZE_BYTES;

/** Kurzfassung der Anpassungen, z. B. „2 ausgeblendet · 1 geändert · 3 eigene“. */
export function overlayZusammenfassung(o: KatalogOverlay | null): string {
    if (!o) return 'Standardkatalog ohne Anpassungen';
    const teile: string[] = [];
    const aus = o.ausgeblendet?.length ?? 0;
    const geaendert = Object.keys(o.geaendert ?? {}).length;
    const eigene = o.eigene?.length ?? 0;
    if (aus) teile.push(`${aus} ausgeblendet`);
    if (geaendert) teile.push(`${geaendert} geändert`);
    if (eigene) teile.push(`${eigene} eigene`);
    if (Object.keys(o.reihenfolge ?? {}).length) teile.push('Reihenfolge angepasst');
    if (o.nachHaeufigkeit === false) teile.push('ohne Sortierung nach Häufigkeit');
    return teile.length ? teile.join(' · ') : 'Eigener Katalog ohne Anpassungen';
}

// ─── Suche im Gesamtkatalog ─────────────────────────────────────────────────

export interface EintragTrefferZeile {
    eintrag: KatalogEintrag;
    /** Steht als Mangel schon in der Zielgruppe. */
    inGruppe: boolean;
    /** Pfade („Absturz › Seitenschutz“) anderer Gruppen, die den Eintrag schon führen. */
    auchIn: string[];
}

/**
 * Leerer Katalog für die Suche: so erscheinen ALLE passenden Einträge des
 * Gesamtkatalogs als Treffer (die Torten-Suche zeigt einen Eintrag, der schon
 * Knoten ist, sonst nur als Knoten). Modulweit, damit der Such-Index gecacht
 * bleibt.
 */
const LEERER_KATALOG: AufgeloesterKatalog = Object.freeze({
    version: 0,
    knoten: new Map<string, AufgeloesterKnoten>(),
    kinder: new Map<string, readonly string[]>([['', []]]),
    ausgeblendet: new Set<string>(),
});

/**
 * Treffer im Gesamtkatalog für „Aus Gesamtkatalog übernehmen“ unter
 * `gruppeId`. „Schon vorhanden“ heißt: ein Standardmangel führt den Eintrag
 * als Quelle, oder ein eigener Mangel trägt dieselbe Überschrift und
 * Beschreibung (übernommene Mängel tragen keinen Verweis — Entscheid 28.09.).
 */
export function eintragTrefferSuchen(
    eingabe: string,
    quelle: Gesamtkatalog | null,
    voll: AufgeloesterKatalog,
    gruppeId: string,
    max = 30,
): EintragTrefferZeile[] {
    if (!quelle || !falten(eingabe ?? '')) return [];
    const nachId = new Map<string, AufgeloesterKnoten[]>();
    const nachText = new Map<string, AufgeloesterKnoten[]>();
    const merken = (karte: Map<string, AufgeloesterKnoten[]>, schluessel: string, k: AufgeloesterKnoten) => {
        const liste = karte.get(schluessel);
        if (liste) liste.push(k);
        else karte.set(schluessel, [k]);
    };
    for (const k of voll.knoten.values()) {
        if (k.typ !== 'mangel' || !k.vorlage) continue;
        if (k.vorlage.eintragId) merken(nachId, k.vorlage.eintragId, k);
        else if (k.herkunft === 'eigen') merken(nachText, textSchluessel(k.vorlage.titel, k.vorlage.text), k);
    }
    const aus: EintragTrefferZeile[] = [];
    for (const t of suchen(eingabe, LEERER_KATALOG, quelle, {}, max)) {
        const eintragId = t.auswahl.vorlage?.eintragId;
        const eintrag = eintragId ? eintragNachId(quelle, eintragId) : null;
        if (!eintrag) continue;
        const knoten = [...(nachId.get(eintrag.id) ?? []), ...(nachText.get(textSchluessel(eintrag.titel, eintrag.text)) ?? [])];
        const auchIn: string[] = [];
        for (const k of knoten) {
            if (k.eltern === gruppeId) continue;
            const pfad = k.pfad.slice(0, -1).join(' › ');
            if (pfad && !auchIn.includes(pfad)) auchIn.push(pfad);
        }
        aus.push({ eintrag, inGruppe: knoten.some((k) => k.eltern === gruppeId), auchIn });
    }
    return aus;
}
